/**
 * Input sampling → per-player InputFrame. The sim consumes only InputFrames,
 * never raw devices, so a future netcode layer can replace this with a stream
 * of remote frames. Frames are plain numbers (serializable).
 */
export interface InputFrame {
  /** Movement axis, already normalized to length <= 1. */
  mx: number;
  my: number;
  dash: boolean;
  /** Edge-triggered UI intents the sim may consume (e.g. use relic). */
  action: boolean;
}

export function emptyInput(): InputFrame {
  return { mx: 0, my: 0, dash: false, action: false };
}

/** Rotate screen-relative movement into the world plane for an isometric camera. */
export function screenToWorldMovement(frame: InputFrame, cameraYaw: number): void {
  const mx = frame.mx;
  const my = frame.my;
  const sin = Math.sin(cameraYaw);
  const cos = Math.cos(cameraYaw);
  frame.mx = mx * sin + my * cos;
  frame.my = -mx * cos + my * sin;
}

import { defaultBindings, isEditableTarget, type BindAction, type Bindings } from './bindings';

export interface PlayerBinding {
  /** 'keyboard' or gamepad index. */
  source: 'keyboard' | number;
}

/**
 * Convert a wheel event's deltaY/deltaMode into a camera-distance step, clamped per event
 * (mirrors `pinchZoomDelta` in ui/touch.ts). A physical mouse fires a few large-magnitude
 * notches, so the clamp reproduces its old flat per-notch step; a trackpad's two-finger
 * scroll or pinch-to-zoom gesture (reported by browsers as wheel events, pinch adding
 * ctrlKey) fires many small-magnitude events instead, so scaling by magnitude rather than
 * just sign keeps the zoom tracking the gesture instead of snapping through the whole
 * 26-44 range in a couple of frames.
 */
export function wheelZoomDelta(deltaY: number, deltaMode: number): number {
  const px = deltaMode === 1 ? deltaY * 16 : deltaMode === 2 ? deltaY * 800 : deltaY;
  return Math.max(-1.5, Math.min(1.5, px * 0.012));
}

export class InputManager {
  private keys = new Set<string>();
  private dashPressed = false;
  private actionPressed = false;
  private bindings: PlayerBinding[] = [{ source: 'keyboard' }];
  /** Remappable key/button layout (src/core/bindings.ts); set from the save by Game. */
  private controls: Bindings = defaultBindings();
  /** Set by UI when a menu is open; sim gets zeroed input. */
  captured = false;
  /** Virtual touch-stick input for player 0 (src/ui/touch.ts). */
  private virtualMx = 0;
  private virtualMy = 0;
  private virtualDash = false;
  private padDashHeld: boolean[] = [];
  private padActionHeld: boolean[] = [];
  /** Non-sim hotkeys (debug overlay etc.) */
  onKeyDown: ((code: string, ev: KeyboardEvent) => void) | null = null;

  constructor(
    target: Window = window,
    private getGamepads: () => ArrayLike<Gamepad | null> = () =>
      typeof navigator !== 'undefined' && navigator.getGamepads ? navigator.getGamepads() : [],
  ) {
    target.addEventListener('keydown', (e) => {
      // Text fields (join codes, names) get every key untouched.
      if (isEditableTarget(e.target)) return;
      // Record every currently held direction before filtering repeat-only
      // actions. This keeps simultaneous directional keys as one input chord.
      this.keys.add(e.code);
      if (e.repeat) {
        return;
      }
      if (this.controls.keyboard.ability.includes(e.code)) this.dashPressed = true;
      if (e.code === 'KeyE') this.actionPressed = true;
      this.onKeyDown?.(e.code, e);
      if (e.code === 'Space' || e.code.startsWith('Arrow') || this.isBound(e.code)) e.preventDefault?.();
    });
    target.addEventListener('keyup', (e) => this.keys.delete(e.code));
    target.addEventListener('blur', () => this.keys.clear());
  }

  setPlayerCount(n: number): void {
    this.bindings = [];
    this.bindings.push({ source: 'keyboard' });
    for (let i = 1; i < n; i++) this.bindings.push({ source: i - 1 });
  }

  isDown(code: string): boolean {
    return this.keys.has(code);
  }

  setControls(controls: Bindings): void {
    this.controls = controls;
  }

  /** True while any key bound to `action` is held. */
  keyHeld(action: BindAction): boolean {
    return this.controls.keyboard[action].some((code) => this.keys.has(code));
  }

  private isBound(code: string): boolean {
    for (const codes of Object.values(this.controls.keyboard)) if (codes.includes(code)) return true;
    return false;
  }

  /** Strength of a gamepad action: the strongest of its bound buttons (analog triggers give 0..1). */
  private padValue(pad: Gamepad, action: BindAction): number {
    let v = 0;
    for (const i of this.controls.gamepad[action]) {
      const b = pad.buttons[i];
      if (b) v = Math.max(v, b.value || (b.pressed ? 1 : 0));
    }
    return v;
  }

  private padPressed(pad: Gamepad, action: BindAction): boolean {
    return this.controls.gamepad[action].some((i) => !!pad.buttons[i]?.pressed);
  }

  /** Presentation-only camera zoom axis (+ = farther): bound keys, then bound pad buttons (default LT in, RT out). */
  zoomAxis(): number {
    const keys = (this.keyHeld('zoomOut') ? 1 : 0) - (this.keyHeld('zoomIn') ? 1 : 0);
    if (keys !== 0) return keys;
    const pads = this.getGamepads();
    const pad = pads[0];
    if (!pad) return 0;
    const axis = this.padValue(pad, 'zoomOut') - this.padValue(pad, 'zoomIn');
    return Math.abs(axis) < 0.08 ? 0 : axis;
  }

  /** Called by touch.ts every frame with the virtual stick's normalized offset and a dash edge. */
  setVirtual(mx: number, my: number, dashEdge: boolean): void {
    this.virtualMx = mx;
    this.virtualMy = my;
    if (dashEdge) this.virtualDash = true;
  }

  /** Presentation controls (currently the house touch UI) can emit an interact edge. */
  triggerAction(): void {
    this.actionPressed = true;
  }

  /** Sample all devices into `out[i]` for player i. Call once per sim tick. */
  sample(out: InputFrame[]): void {
    const pads = this.getGamepads();
    const dashEdges: boolean[] = [];
    const actionEdges: boolean[] = [];
    for (let i = 0; i < pads.length; i++) {
      const pad = pads[i];
      const dashDown = !!pad && this.padPressed(pad, 'ability');
      const actionDown = !!pad?.buttons[2]?.pressed;
      dashEdges[i] = dashDown && !this.padDashHeld[i];
      actionEdges[i] = actionDown && !this.padActionHeld[i];
    }
    for (let i = 0; i < out.length; i++) {
      const f = out[i];
      f.mx = 0;
      f.my = 0;
      f.dash = false;
      f.action = false;
      if (this.captured) continue;
      const b = this.bindings[i];
      if (!b) continue;
      if (b.source === 'keyboard') {
        if (this.keyHeld('moveLeft')) f.mx -= 1;
        if (this.keyHeld('moveRight')) f.mx += 1;
        if (this.keyHeld('moveUp')) f.my -= 1;
        if (this.keyHeld('moveDown')) f.my += 1;
        f.dash = this.dashPressed;
        f.action = this.actionPressed;
        // keyboard player also gets gamepad 0 as fallback
        const p0 = pads[0];
        if (p0 && f.mx === 0 && f.my === 0) this.readPad(p0, f, dashEdges[0], actionEdges[0]);
        // virtual touch stick/dash merges into player 0 when non-zero
        if (this.virtualMx !== 0 || this.virtualMy !== 0) {
          f.mx = this.virtualMx;
          f.my = this.virtualMy;
        }
        if (this.virtualDash) f.dash = true;
      } else {
        const p = pads[b.source];
        if (p) this.readPad(p, f, dashEdges[b.source], actionEdges[b.source]);
      }
      const l = Math.hypot(f.mx, f.my);
      if (l > 1) {
        f.mx /= l;
        f.my /= l;
      }
    }
    this.dashPressed = false;
    this.actionPressed = false;
    this.virtualDash = false;
    for (let i = 0; i < pads.length; i++) {
      const pad = pads[i];
      this.padDashHeld[i] = !!pad && this.padPressed(pad, 'ability');
      this.padActionHeld[i] = !!pad?.buttons[2]?.pressed;
    }
  }

  private readPad(p: Gamepad, f: InputFrame, dashEdge = false, actionEdge = false): void {
    const dz = 0.2;
    const ax = p.axes[0] ?? 0;
    const ay = p.axes[1] ?? 0;
    if (Math.abs(ax) > dz) f.mx = ax;
    if (Math.abs(ay) > dz) f.my = ay;
    if (f.mx === 0 && f.my === 0) {
      // Bound digital directions (default: D-pad).
      f.mx = (this.padPressed(p, 'moveRight') ? 1 : 0) - (this.padPressed(p, 'moveLeft') ? 1 : 0);
      f.my = (this.padPressed(p, 'moveDown') ? 1 : 0) - (this.padPressed(p, 'moveUp') ? 1 : 0);
    }
    if (dashEdge) f.dash = true;
    if (actionEdge) f.action = true;
  }
}
